import { useCallback, useMemo, useRef } from 'react';
import {
  MulticamAngle,
  MulticamAnnotation,
  addMulticamAnnotation,
  deleteMulticamAnnotation,
} from '../../graphqlService';
import type { TranscriptMark } from '../../utils/transcriptMarks';
import type { TranscriptMarkingProps } from '../Transcript/transcriptMarking';
import { MarkNotify, TranscriptMarkStore, useTranscriptMarks } from '../Transcript/useTranscriptMarks';
import {
  AnnotationDraft,
  describeSaveError,
  draftFromAnnotation,
  isCut,
  normalizeAnnotations,
  sortAnnotations,
  toMutationInput,
  toPanelAnnotation,
} from './multicamStudioModel';

/**
 * Marking the transcript in the multicam studio. Remove and Short are NOTEs
 * about every angle (they direct the program), in the categories `remove`
 * and `short`. Like every other change in the studio, each one is its own
 * request through the studio's queue, so the server sees them in order.
 */

interface Options {
  multicamSetId?: string;
  annotations: MulticamAnnotation[];
  angles: MulticamAngle[];
  /** The set was submitted: marks show, read-only. */
  locked: boolean;
  /** Notes have loaded and can be changed. */
  editable: boolean;
  notesStatus: 'loading' | 'ready' | 'error';
  selectedId: string | null;
  setSelectedId: React.Dispatch<React.SetStateAction<string | null>>;
  setAnnotations: React.Dispatch<React.SetStateAction<MulticamAnnotation[]>>;
  /** The studio's request queue. */
  runInOrder: <T>(work: () => Promise<T>) => Promise<T>;
  /** Marks a newer request for an annotation, so a slower older answer can't bring it back. */
  nextRequest: (id: string) => number;
  notify: MarkNotify;
  jumpTo: (seconds: number, angleKey?: string | null) => void;
  openEdit: (id: string) => void;
}

export function useMulticamTranscriptMarks({
  multicamSetId,
  annotations,
  angles,
  locked,
  editable,
  notesStatus,
  selectedId,
  setSelectedId,
  setAnnotations,
  runInOrder,
  nextRequest,
  notify,
  jumpTo,
  openEdit,
}: Options): { marking: TranscriptMarkingProps; restore: (annotation: MulticamAnnotation) => Promise<void> } {
  const latest = useRef(annotations);
  latest.current = annotations;

  const notes = useMemo(
    () => annotations.filter((annotation) => !isCut(annotation)).map((annotation) => toPanelAnnotation(annotation, angles)),
    [annotations, angles]
  );

  const addDraft = useCallback(async (draft: AnnotationDraft): Promise<MulticamAnnotation | null> => {
    if (!multicamSetId) return null;
    const { endTimeMs, ...rest } = toMutationInput(draft);
    try {
      const created = await runInOrder(() =>
        addMulticamAnnotation(multicamSetId, endTimeMs === null ? rest : { ...rest, endTimeMs })
      );
      const [saved] = normalizeAnnotations(created ? [created] : []);
      if (!saved) throw new Error('The saved annotation was not returned.');
      setAnnotations((previous) => sortAnnotations([...previous.filter((annotation) => annotation.id !== saved.id), saved]));
      return saved;
    } catch (error) {
      console.error('Failed to save a transcript mark:', error);
      notify(describeSaveError(error, 'add-note'), 'error');
      return null;
    }
  }, [multicamSetId, notify, runInOrder, setAnnotations]);

  /** Puts a deleted note back (as a new note with the same content). */
  const restore = useCallback(async (annotation: MulticamAnnotation) => {
    const saved = await addDraft(draftFromAnnotation(annotation));
    if (saved) setSelectedId(saved.id);
  }, [addDraft, setSelectedId]);

  const store = useMemo<TranscriptMarkStore>(() => ({
    add: async (note) => {
      const saved = await addDraft({
        type: 'NOTE',
        time: note.time,
        endTime: note.endTime ?? null,
        text: note.text,
        category: note.category ?? null,
        targetAngleKey: null,
        storedColor: note.color ?? null,
      });
      if (!saved) return null;
      setSelectedId(saved.id);
      return saved.id;
    },
    remove: async (id) => {
      const target = latest.current.find((annotation) => annotation.id === id);
      if (!target) return null;
      // A slower update still on its way must not bring it back.
      nextRequest(id);
      try {
        await runInOrder(() => deleteMulticamAnnotation(id));
      } catch (error) {
        console.error('Failed to delete a transcript mark:', error);
        notify(describeSaveError(error, 'delete'), 'error');
        return null;
      }
      setAnnotations((previous) => previous.filter((annotation) => annotation.id !== id));
      setSelectedId((current) => (current === id ? null : current));
      return () => restore(target);
    },
  }), [addDraft, nextRequest, notify, restore, runInOrder, setAnnotations, setSelectedId]);

  const onSelect = useCallback((mark: TranscriptMark) => {
    setSelectedId(mark.id);
    jumpTo(mark.start, latest.current.find((annotation) => annotation.id === mark.id)?.targetAngleKey);
  }, [jumpTo, setSelectedId]);
  const onClearSelection = useCallback(() => setSelectedId(null), [setSelectedId]);
  const onEdit = useCallback((mark: TranscriptMark) => openEdit(mark.id), [openEdit]);

  let disabledReason: string | null = null;
  if (!editable && !locked) {
    disabledReason = notesStatus === 'error'
      ? 'Words can be marked once this set’s cuts and notes load. Use Retry above.'
      : 'Words can be marked once this set’s cuts and notes have loaded.';
  }

  const marking = useTranscriptMarks({
    notes,
    readOnly: locked,
    disabledReason,
    selectedId,
    store,
    notify,
    onSelect,
    onClearSelection,
    onEdit,
  });
  return { marking, restore };
}
